import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { UpsertDailyEntryInput } from "@consistency-circle/shared";
import { CheckIcon, MoonIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CloseOutStep } from "./components/CloseOutStep";
import { StepCard } from "./components/StepCard";
import { EMPTY_ENTRY } from "./constants";
import type { Step } from "./types";
import { apiClient } from "../../lib/api";
import { deriveTimeline } from "../dashboard/utils";

export function ShutdownPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step | null>(null);
  const [tomorrowTask, setTomorrowTask] = useState<string | null>(null);
  const [note, setNote] = useState("");

  const { data: dashboard } = useQuery({
    queryKey: ["today-dashboard"],
    queryFn: () => apiClient.getTodayDashboard(),
  });

  const { data: todayEntry } = useQuery({
    queryKey: ["today-entry"],
    queryFn: () => apiClient.getTodayEntry(),
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      const existing = todayEntry?.entry;
      const base: UpsertDailyEntryInput = existing
        ? {
            wokeOnTime: existing.wokeOnTime,
            walked: existing.walked,
            deepWorkDone: existing.deepWorkDone,
            shutdownNoteDone: existing.shutdownNoteDone,
            phoneFreeBedtime: existing.phoneFreeBedtime,
            mood: existing.mood,
            energy: existing.energy,
            focus: existing.focus,
            brainFog: existing.brainFog,
            motivation: existing.motivation,
            sleepHours: existing.sleepHours,
            sleepQuality: existing.sleepQuality,
            dietQuality: existing.dietQuality,
            exerciseQuality: existing.exerciseQuality,
            notes: existing.notes,
            tomorrowFirstTask: existing.tomorrowFirstTask,
          }
        : { ...EMPTY_ENTRY };

      const task = (tomorrowTask ?? existing?.tomorrowFirstTask ?? "").trim();
      const noteText = note.trim();

      return apiClient.upsertDailyEntry(todayEntry!.localDate, {
        ...base,
        shutdownNoteDone: true,
        tomorrowFirstTask: task.length > 0 ? task : null,
        notes: noteText.length > 0 ? noteText : base.notes,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["today-entry"] });
      setStep("done");
    },
  });

  if (!dashboard || !todayEntry) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-7 h-7 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const timeline = deriveTimeline(dashboard);
  const openSlots = [
    ...timeline.recover,
    ...(timeline.hero ? [timeline.hero] : []),
    ...timeline.upNext,
  ];
  const activeStep: Step = step ?? (openSlots.length > 0 ? "close-out" : "tomorrow");
  const taskValue = tomorrowTask ?? todayEntry.entry?.tomorrowFirstTask ?? "";

  return (
    <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
      <header className="flex items-center gap-3">
        <span className="shrink-0 w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
          <MoonIcon className="w-4.5 h-4.5 text-primary" />
        </span>
        <div>
          <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
            Evening shutdown
          </p>
          <h1 className="text-xl font-semibold tracking-tight">Close out the day</h1>
        </div>
      </header>

      {activeStep === "close-out" && (
        <CloseOutStep openSlots={openSlots} onDone={() => setStep("tomorrow")} />
      )}

      {activeStep === "tomorrow" && (
        <StepCard
          title="What's the first thing tomorrow?"
          subtitle="One task. Decide it now so the morning doesn't have to."
        >
          <Input
            autoFocus
            value={taskValue}
            maxLength={240}
            onChange={(event) => setTomorrowTask(event.target.value)}
            placeholder="e.g. 20-minute walk before coffee"
            onKeyDown={(event) => {
              if (event.key === "Enter") setStep("note");
            }}
          />
          <div className="flex items-center justify-between">
            <button
              onClick={() => setStep("note")}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-2 rounded-lg hover:bg-muted"
            >
              Skip
            </button>
            <Button onClick={() => setStep("note")}>Continue</Button>
          </div>
        </StepCard>
      )}

      {activeStep === "note" && (
        <StepCard
          title="One line about today"
          subtitle="Optional. Whatever is worth remembering."
        >
          <Input
            autoFocus
            value={note}
            maxLength={1000}
            onChange={(event) => setNote(event.target.value)}
            placeholder="e.g. Slow start, strong finish."
            onKeyDown={(event) => {
              if (event.key === "Enter" && !saveMutation.isPending) saveMutation.mutate();
            }}
          />
          {saveMutation.isError && (
            <p className="text-sm text-destructive">Failed to save. Try again.</p>
          )}
          <div className="flex items-center justify-end">
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving…" : "Finish the day"}
            </Button>
          </div>
        </StepCard>
      )}

      {activeStep === "done" && (
        <div className="rounded-2xl border border-border bg-accent/40 px-5 py-6 text-center space-y-3">
          <span className="mx-auto w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
            <CheckIcon className="w-4.5 h-4.5 text-primary" strokeWidth={3} />
          </span>
          <div>
            <p className="text-sm font-semibold">The day is closed.</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {taskValue.trim()
                ? `Tomorrow starts with: ${taskValue.trim()}`
                : "Tomorrow is a fresh start."}
            </p>
          </div>
          <Button variant="secondary" onClick={() => void navigate({ to: "/dashboard" })}>
            Back to dashboard
          </Button>
        </div>
      )}

      {activeStep !== "done" && (
        <p className="text-center text-xs text-muted-foreground">
          <Link to="/dashboard" className="hover:text-foreground transition-colors">
            Not now — back to dashboard
          </Link>
        </p>
      )}
    </div>
  );
}
